
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useExtensionStatus } from '../hooks/useAppNavigation';
import { Header } from '../components/Header';
import {
  buildAuthModalPath,
  resolveApiBaseUrl,
  resolveAuthToken,
  resolveAuthUser,
  resolveCollectOptions,
  shouldFallbackToPolling,
  syncAuthToExtension,
} from '../lib/utils';
import {
  Public, AutoFixHigh, CheckCircle, Settings, Link as LinkIcon,
  CloudUpload, ArrowForward, Info, Download, ExtensionOff, Login
} from '@mui/icons-material';
import type { ExtensionStatus } from '../types';

interface RecentTask {
  taskId: string;
  sourceUrl: string;
  createdAt: string;
  status: 'processing' | 'queued' | 'completed' | 'failed';
  collectionId?: string | null;
}

interface ImportSuccessPayload {
  taskId: string;
  collectionId: string | null;
  url: string;
}

export type ImportDemoState = {
  url?: string;
  importStatus?: 'idle' | 'submitting' | 'error';
  importMessage?: string;
  isAuthenticated?: boolean;
  isConnected?: boolean;
  extensionStatus?: ExtensionStatus;
  recentTask?: RecentTask | null;
  successPayload?: ImportSuccessPayload | null;
  isSuccessModalOpen?: boolean;
};

type ImportScreenProps = {
  demoState?: ImportDemoState;
};

export const ImportScreen: React.FC<ImportScreenProps> = ({ demoState }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { status: extensionStatus } = useExtensionStatus();
  const [urlState, setUrl] = useState('');
  const [importStatusState, setImportStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [importMessageState, setImportMessage] = useState('');
  const [isAuthenticatedState, setIsAuthenticated] = useState(false);
  const [recentTaskState, setRecentTask] = useState<RecentTask | null>(null);
  const [successPayloadState, setSuccessPayload] = useState<ImportSuccessPayload | null>(null);
  const [isSuccessModalOpenState, setIsSuccessModalOpen] = useState(false);

  const demo = demoState;
  const isDemo = Boolean(demo);
  const url = demo?.url ?? urlState;
  const importStatus = demo?.importStatus ?? importStatusState;
  const importMessage = demo?.importMessage ?? importMessageState;
  const isAuthenticated = demo?.isAuthenticated ?? isAuthenticatedState;
  const recentTask = demo?.recentTask ?? recentTaskState;
  const successPayload = demo?.successPayload ?? successPayloadState;
  const isSuccessModalOpen = demo?.isSuccessModalOpen ?? isSuccessModalOpenState;
  const extensionStatusValue = demo?.extensionStatus ?? extensionStatus;
  const isConnected = demo?.isConnected ?? extensionStatusValue === 'CONNECTED';
  const isSubmitting = importStatus === 'submitting';

  const resolveAuthFlags = () => {
    if (typeof window === 'undefined') {
      return { isAuthenticated: false };
    }
    const primaryKeys = ['token', 'access_token', 'auth_token', 'authToken', 'ozon_token'];
    const hasPrimaryToken = primaryKeys.some((key) => localStorage.getItem(key));
    const hasAuthPayload = Boolean(localStorage.getItem('auth'));
    const isAuthenticatedUser = hasPrimaryToken || hasAuthPayload;
    return { isAuthenticated: isAuthenticatedUser };
  };

  useEffect(() => {
    if (isDemo) {
      return;
    }
    const syncAuth = () => {
      const { isAuthenticated } = resolveAuthFlags();
      setIsAuthenticated(isAuthenticated);
    };
    syncAuth();
    if (typeof window === 'undefined') {
      return;
    }
    const storedTask = localStorage.getItem('recent_import_task');
    if (storedTask) {
      try {
        const parsed = JSON.parse(storedTask) as RecentTask;
        if (parsed?.taskId && parsed?.sourceUrl) {
          setRecentTask(parsed);
        }
      } catch (error) {
        // ignore parse errors
      }
    }
    const handleAuthUpdated = () => syncAuth();
    const handleFocus = () => syncAuth();
    window.addEventListener('auth:updated', handleAuthUpdated);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('auth:updated', handleAuthUpdated);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isDemo, location.pathname, location.search]);

  const requestGuestSession = async (apiBaseUrl: string) => {
    const response = await fetch(`${apiBaseUrl}/api/v1/auth/guest`, { method: 'POST' });
    if (!response.ok) {
      return null;
    }

    const payload = await response.json().catch(() => null);
    const token = payload?.token;
    if (!token) {
      return null;
    }

    try {
      localStorage.setItem('guest_token', token);
      if (payload?.user) {
        localStorage.setItem('guest_user', JSON.stringify(payload.user));
      }
    } catch (error) {
      // ignore storage errors
    }

    return token;
  };

  const handleImport = async () => {
    if (isDemo) {
      return;
    }
    if (!isConnected) {
      setImportStatus('error');
      setImportMessage(t('import.extMissingDesc'));
      return;
    }

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setImportStatus('error');
      setImportMessage(t('import.urlRequired'));
      return;
    }

    setImportStatus('submitting');
    setImportMessage(t('import.submitting'));

    try {
      const apiBaseUrl = resolveApiBaseUrl();
      let token = resolveAuthToken();
      if (!token) {
        token = await requestGuestSession(apiBaseUrl);
      }

      if (!token) {
        setImportStatus('error');
        setImportMessage(t('import.guestFailed'));
        return;
      }
      const authFlags = resolveAuthFlags();
      setIsAuthenticated(authFlags.isAuthenticated);
      await syncAuthToExtension(token, resolveAuthUser(), apiBaseUrl);

      let usePolling = true;
      try {
        const statusResponse = await fetch(`${apiBaseUrl}/api/v1/collect/plugin/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (statusResponse.ok) {
          const statusData = await statusResponse.json().catch(() => null);
          usePolling = !statusData?.connected;
        }
      } catch (error) {
        usePolling = true;
      }

      const buildCollectRequest = (polling: boolean) => ({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: trimmedUrl,
          options: resolveCollectOptions(polling),
        })
      });

      let response = await fetch(`${apiBaseUrl}/api/v1/collect`, buildCollectRequest(usePolling));
      let data = await response.json().catch(() => ({}));

      if (!response.ok && shouldFallbackToPolling(data) && !usePolling) {
        response = await fetch(`${apiBaseUrl}/api/v1/collect`, buildCollectRequest(true));
        data = await response.json().catch(() => ({}));
      }

      if (!response.ok) {
        const errorMessage = data?.detail?.error || data?.error || t('import.collectFailed');
        setImportStatus('error');
        setImportMessage(errorMessage);
        return;
      }

      setImportStatus('idle');
      setImportMessage('');
      const payload: ImportSuccessPayload = {
        taskId: data?.task_id || '',
        url: trimmedUrl,
        collectionId: data?.collection_id || null
      };
      setSuccessPayload(payload);
      setIsSuccessModalOpen(true);
      const recentPayload: RecentTask = {
        taskId: payload.taskId,
        sourceUrl: payload.url,
        createdAt: new Date().toISOString(),
        status: 'processing',
        collectionId: payload.collectionId
      };
      setRecentTask(recentPayload);
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('recent_import_task', JSON.stringify(recentPayload));
        } catch (error) {
          // ignore storage errors
        }
      }
    } catch (error) {
      setImportStatus('error');
      setImportMessage(t('import.collectFailed'));
    }
  };

  const handleOpenTaskCenter = () => {
    if (!isAuthenticated) {
      navigate(buildAuthModalPath(location.pathname, location.search, 'login'));
      return;
    }
    navigate('/task-center');
  };

  const handleContinueImport = () => {
    if (!successPayload) {
      setIsSuccessModalOpen(false);
      return;
    }
    setIsSuccessModalOpen(false);
    navigate('/import/preview', {
      state: {
        taskId: successPayload.taskId,
        url: successPayload.url,
        collectionId: successPayload.collectionId
      }
    });
  };

  const formatRecentDate = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const locale = language === 'zh' ? 'zh-CN' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const recentStatusLabel = (status: RecentTask['status']) => {
    switch (status) {
      case 'completed':
        return t('import.recent.status.completed');
      case 'failed':
        return t('import.recent.status.failed');
      case 'queued':
        return t('import.recent.status.queued');
      default:
        return t('import.recent.status.processing');
    }
  };

  const recentStatusStyle = (status: RecentTask['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-600 border border-emerald-100';
      case 'failed':
        return 'bg-red-50 text-red-600 border border-red-100';
      case 'queued':
        return 'bg-amber-50 text-amber-600 border border-amber-100';
      default:
        return 'bg-blue-50 text-blue-600 border border-blue-100';
    }
  };

  const rightContent = (
     <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-500">
        <Public fontSize="small" className="text-primary" />
        {t('import.targetGlobal')}
     </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      <Header
        breadcrumbs={[
            { label: t('workspace.nav'), path: '/workspace' },
            { label: t('import.nav') }
        ]}
        rightElement={rightContent}
      />

      <main className={`max-w-6xl mx-auto px-6 py-16 ${isSuccessModalOpen ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="mb-12 flex flex-col items-center gap-6 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-primary/5 text-primary mb-6 ring-8 ring-primary/5">
              <AutoFixHigh fontSize="large" />
            </div>
            <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-4">{t('import.title')}</h1>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">{t('import.subtitle')}</p>
          </div>
          <div className="w-full flex flex-col items-center gap-2">
            <button
              onClick={handleOpenTaskCenter}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold border transition-all ${isAuthenticated ? 'bg-white border-slate-200 text-slate-700 hover:border-primary hover:text-primary shadow-sm' : 'bg-slate-100 border-slate-200 text-slate-400'}`}
            >
              {isAuthenticated ? <span className="material-symbols-outlined text-base">insights</span> : <Login fontSize="small" />}
              {t('import.taskCenterEntry')}
              <ArrowForward fontSize="small" />
            </button>
            {!isAuthenticated && (
              <p className="text-xs text-slate-400 text-center">{t('import.taskCenterHint')}</p>
            )}
          </div>
        </div>

        {isConnected ? (
          <div className="mb-8 bg-emerald-50 border border-emerald-100 rounded-[2.5rem] p-8 flex flex-col md:flex-row items-center justify-between gap-8 shadow-sm animate-in zoom-in duration-500">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center shrink-0 border border-emerald-50 text-emerald-500">
                <CheckCircle fontSize="large" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 flex items-center gap-2">
                  {t('import.extActive')}
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-[10px] font-black text-emerald-600 uppercase tracking-tighter border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    {t('import.connected')}
                  </span>
                </h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed font-medium">{t('import.extActiveDesc')}</p>
              </div>
            </div>
            <button
              onClick={() => alert('Opening extension settings...')}
              className="flex items-center gap-2 px-6 py-3 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold text-sm shadow-sm hover:bg-slate-50 transition-all active:scale-95"
            >
              <Settings fontSize="small" /> {t('import.extSettings')}
            </button>
          </div>
        ) : (
          <div className="mb-8 bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded-[2.5rem] p-8 flex flex-col md:flex-row items-center justify-between gap-8 shadow-sm animate-in zoom-in duration-500">
             <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center shrink-0 border border-amber-100 text-amber-500">
                   {extensionStatusValue === 'OFFLINE' ? <ExtensionOff fontSize="large" /> : <Info fontSize="large" className="text-red-500" />}
                </div>
                <div>
                   <h3 className="font-black text-slate-900 flex items-center gap-2">
                      {t('import.extMissing')}
                      <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-[10px] font-black text-red-600 uppercase tracking-tighter border border-red-200">
                        {extensionStatusValue === 'OFFLINE' ? 'OFFLINE' : 'NOT DETECTED'}
                      </span>
                   </h3>
                   <p className="text-sm text-slate-600 mt-2 leading-relaxed font-medium">{t('import.extMissingDesc')}</p>
                </div>
             </div>
             <button onClick={() => alert(t('import.downloadGuide'))} className="flex items-center gap-2 px-8 py-3.5 bg-slate-900 text-white rounded-xl font-black text-sm shadow-xl hover:opacity-90 transition-all active:scale-95 whitespace-nowrap">
                <Download fontSize="small" /> {t('import.downloadBtn')}
             </button>
          </div>
        )}

        {recentTask && (
          <div className="mb-8 bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t('import.recent.title')}</p>
                <h3 className="text-xl font-black text-slate-900 mt-2">{t('import.recent.subtitle')}</h3>
              </div>
              <button
                onClick={handleOpenTaskCenter}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold border transition-all ${isAuthenticated ? 'bg-slate-900 text-white border-slate-900 hover:opacity-90' : 'bg-slate-100 text-slate-400 border-slate-200'}`}
              >
                {isAuthenticated ? t('import.recent.viewTaskCenter') : t('import.recent.loginToView')}
                <ArrowForward fontSize="small" />
              </button>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3 text-sm">
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.recent.taskId')}</p>
                <p className="mt-2 font-semibold text-slate-700">{recentTask.taskId || '--'}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.recent.source')}</p>
                <p className="mt-2 font-semibold text-slate-700 truncate max-w-[280px]" title={recentTask.sourceUrl}>
                  {recentTask.sourceUrl || '--'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.recent.createdAt')}</p>
                <p className="mt-2 font-semibold text-slate-700">{formatRecentDate(recentTask.createdAt)}</p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${recentStatusStyle(recentTask.status)}`}>
                {recentStatusLabel(recentTask.status)}
              </span>
              <span className="text-xs text-slate-500">{t('import.recent.statusHint')}</span>
            </div>
            {!isAuthenticated && (
              <p className="mt-4 text-xs text-slate-400">{t('import.taskCenterHint')}</p>
            )}
          </div>
        )}

        <div className="space-y-8">
          <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-primary border border-blue-100">
                <LinkIcon />
              </div>
              <div>
                <h2 className="font-black text-xl text-slate-900 tracking-tight">{t('import.urlLabel')}</h2>
                <p className="text-sm text-slate-400 font-medium tracking-tight">{t('import.urlSub')}</p>
              </div>
            </div>

            <div className="relative group">
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className={`w-full pl-8 pr-44 py-6 rounded-2xl text-lg transition-all outline-none border-2 font-medium ${isConnected ? 'bg-white border-slate-300 focus:border-primary' : 'bg-slate-50 border-slate-300 text-slate-300 cursor-not-allowed'}`}
                placeholder={t('import.urlPlaceholder')}
                type="text"
                disabled={!isConnected || isSubmitting}
              />
              <button
                onClick={handleImport}
                disabled={!isConnected || isSubmitting}
                className={`absolute right-3 top-3 bottom-3 px-12 rounded-xl font-black text-sm shadow-lg transition-all flex items-center gap-2 active:scale-95 disabled:active:scale-100 ${isConnected ? 'bg-primary text-white hover:bg-blue-700 shadow-blue-500/30' : 'bg-slate-200 text-slate-400 cursor-default'}`}
              >
                {isSubmitting ? t('import.submitting') : t('import.importBtn')} <ArrowForward fontSize="small" />
              </button>
            </div>
            {importMessage && (
              <p className={`mt-3 text-xs font-semibold ${importStatus === 'error' ? 'text-red-500' : 'text-slate-500'}`}>
                {importMessage}
              </p>
            )}
          </div>

          <div className="flex items-center gap-6 text-slate-200">
            <div className="h-px flex-1 bg-slate-200"></div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">{t('import.alternative')}</span>
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>

          <div
            onClick={() => alert('Opening file picker...')}
            className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-16 text-center hover:border-primary hover:bg-blue-50/20 group cursor-pointer transition-all active:scale-[0.99]"
          >
            <div className="size-20 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/10 group-hover:text-primary transition-all">
              <CloudUpload fontSize="large" />
            </div>
            <h3 className="font-black text-2xl text-slate-900 mb-2">{t('import.uploadTitle')}</h3>
            <p className="text-slate-500 font-medium mb-8">{t('import.uploadSub')}</p>
            <button className="px-8 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-black uppercase tracking-widest text-slate-600 transition-colors pointer-events-none group-active:scale-95">{t('import.chooseFiles')}</button>
          </div>
        </div>
      </main>

      {isSuccessModalOpen && successPayload && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">{t('import.success.title')}</h3>
                <p className="text-sm text-slate-500 mt-1">{t('import.success.subtitle')}</p>
              </div>
              <button
                onClick={() => setIsSuccessModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>
            <div className="p-8 space-y-6 overflow-y-auto">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                <div className="w-10 h-10 rounded-full bg-white text-emerald-500 flex items-center justify-center border border-emerald-100">
                  <CheckCircle fontSize="medium" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{t('import.success.statusProcessing')}</p>
                  <p className="text-xs text-slate-500">{t('import.success.statusHint')}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3 text-sm">
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.success.taskId')}</p>
                  <p className="mt-2 font-semibold text-slate-700">{successPayload.taskId || '--'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.success.source')}</p>
                  <p className="mt-2 font-semibold text-slate-700 truncate max-w-[260px]" title={successPayload.url}>
                    {successPayload.url || '--'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('import.success.status')}</p>
                  <p className="mt-2 font-semibold text-slate-700">{t('import.recent.status.processing')}</p>
                </div>
              </div>
              {!isAuthenticated && (
                <p className="text-xs text-slate-400">{t('import.success.loginHint')}</p>
              )}
            </div>
            <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3">
              <button
                onClick={handleContinueImport}
                className="h-10 px-6 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors"
              >
                {t('import.success.continue')}
              </button>
              <button
                onClick={handleOpenTaskCenter}
                className="h-10 px-8 rounded-lg bg-primary text-white text-sm font-bold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-lg">track_changes</span>
                <span>{isAuthenticated ? t('import.success.goTaskCenter') : t('import.success.loginTaskCenter')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
